import { useEffect, useLayoutEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { API } from "@/apiConfig";
const fadeUp = (delay = 0) => ({ initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.55, delay, ease: "easeOut" } });

const DEFAULT_PAGE = {
  page_kicker: "Contact",
  page_title: "Écrivez-moi",
  intro_text: "Je réponds à tous les messages dans les 48h ouvrées.",
  header_image: "",
  header_image_alt: "Contact Eudemonie",
  contact_title: "Informations de contact",
  discovery_title: "Échange découverte gratuit",
  discovery_text: "Je propose un appel téléphonique de 20 minutes gratuit pour répondre à vos questions avant de prendre rendez-vous.",
  name_label: "Nom *",
  email_label: "Email *",
  phone_label: "Téléphone",
  subject_label: "Sujet",
  message_label: "Message *",
  submit_label: "Envoyer le message",
  success_title: "Message envoyé !",
  success_text: "Je vous répondrai dans les 48h ouvrées. Merci de votre confiance.",
};

export default function Contact({ initialPage, siteSettings }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [settings, setSettings] = useState({ phone: "+33 (0)7 67 91 73 70", email: "celine@eudemonie-sophro.fr", address: "Vanves, 92170 Hauts-de-Seine" });

  useLayoutEffect(() => {
    if (initialPage?.content) setPage(prev => ({ ...prev, ...initialPage.content }));
    if (siteSettings) setSettings(prev => ({ ...prev, ...siteSettings }));
  }, [initialPage, siteSettings]);

  useEffect(() => {
    axios.get(`${API}/public/pages/contact`).then(r => { if (r.data?.content) setPage(prev => ({ ...prev, ...r.data.content })); }).catch(() => {});
    axios.get(`${API}/public/settings`).then(r => { if (r.data) setSettings(prev => ({ ...prev, ...r.data })); }).catch(() => {});
  }, []);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Veuillez remplir les champs obligatoires."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/public/contact`, form);
      setSent(true);
      toast.success("Votre message a été envoyé !");
    } catch {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20">
      <section className="section-gap bg-bone">
        <div className="section-container">
          <div className="max-w-2xl">
            <motion.p {...fadeUp()} className="font-manrope text-xs text-sage tracking-widest uppercase mb-3">{page.page_kicker}</motion.p>
            <motion.h1 {...fadeUp(0.1)} className="font-playfair text-5xl md:text-6xl text-charcoal mb-4">{page.page_title}</motion.h1>
            <motion.p {...fadeUp(0.2)} className="font-manrope text-charcoal-muted text-lg">{page.intro_text}</motion.p>
          </div>
          {page.header_image && <motion.img {...fadeUp(0.25)} src={page.header_image} alt={page.header_image_alt} className="mt-10 rounded-[2rem] w-full max-h-[360px] object-cover shadow-lg" />}
        </div>
      </section>

      <section className="section-gap bg-bone-light">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Info */}
            <motion.div {...fadeUp()} className="lg:col-span-2 space-y-6">
              <div>
                <h2 className="font-playfair text-2xl text-charcoal mb-6">{page.contact_title}</h2>
                {[
                  { icon: MapPin, label: "Cabinet", value: settings.address },
                  { icon: Phone, label: "Téléphone", value: settings.phone, href: `tel:${settings.phone?.replace(/\s/g,'')}` },
                  { icon: Mail, label: "Email", value: settings.email, href: `mailto:${settings.email}` },
                ].map((c, i) => (
                  <div key={i} className="flex items-start gap-4 pb-4 border-b border-sage/10 last:border-0">
                    <div className="w-9 h-9 rounded-lg bg-sage/10 flex items-center justify-center shrink-0 mt-0.5">
                      <c.icon size={16} className="text-sage" />
                    </div>
                    <div>
                      <p className="font-manrope text-xs text-charcoal-muted mb-0.5">{c.label}</p>
                      {c.href ? (
                        <a href={c.href} className="font-manrope text-sm text-charcoal hover:text-sage transition-colors">{c.value}</a>
                      ) : (
                        <p className="font-manrope text-sm text-charcoal">{c.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-sage/5 rounded-2xl p-6 border border-sage/10">
                <h3 className="font-playfair text-lg text-charcoal mb-2">{page.discovery_title}</h3>
                <p className="font-manrope text-sm text-charcoal-muted">{page.discovery_text}</p>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div {...fadeUp(0.2)} className="lg:col-span-3">
              {sent ? (
                <div className="card-soft text-center py-16" data-testid="contact-success">
                  <div className="w-16 h-16 rounded-full bg-sage/10 flex items-center justify-center mx-auto mb-4">
                    <Check size={28} className="text-sage" />
                  </div>
                  <h3 className="font-playfair text-2xl text-charcoal mb-2">{page.success_title}</h3>
                  <p className="font-manrope text-charcoal-muted">{page.success_text}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="card-soft space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">{page.name_label}</label>
                      <input name="name" value={form.name} onChange={handleChange} placeholder="Votre nom" data-testid="contact-name"
                        className="w-full bg-bone/50 border border-sage/20 focus:border-sage focus:ring-1 focus:ring-sage/30 rounded-xl px-4 py-3 font-manrope text-sm text-charcoal placeholder:text-charcoal-soft outline-none transition-all" />
                    </div>
                    <div>
                      <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">{page.email_label}</label>
                      <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="votre@email.fr" data-testid="contact-email"
                        className="w-full bg-bone/50 border border-sage/20 focus:border-sage focus:ring-1 focus:ring-sage/30 rounded-xl px-4 py-3 font-manrope text-sm text-charcoal placeholder:text-charcoal-soft outline-none transition-all" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">{page.phone_label}</label>
                      <input name="phone" value={form.phone} onChange={handleChange} placeholder="+33 (0)7 67 91 73 70" data-testid="contact-phone"
                        className="w-full bg-bone/50 border border-sage/20 focus:border-sage focus:ring-1 focus:ring-sage/30 rounded-xl px-4 py-3 font-manrope text-sm text-charcoal placeholder:text-charcoal-soft outline-none transition-all" />
                    </div>
                    <div>
                      <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">{page.subject_label}</label>
                      <input name="subject" value={form.subject} onChange={handleChange} placeholder="Objet de votre message" data-testid="contact-subject"
                        className="w-full bg-bone/50 border border-sage/20 focus:border-sage focus:ring-1 focus:ring-sage/30 rounded-xl px-4 py-3 font-manrope text-sm text-charcoal placeholder:text-charcoal-soft outline-none transition-all" />
                    </div>
                  </div>
                  <div>
                    <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">{page.message_label}</label>
                    <textarea name="message" value={form.message} onChange={handleChange} rows={5} placeholder="Décrivez votre besoin..." data-testid="contact-message"
                      className="w-full bg-bone/50 border border-sage/20 focus:border-sage focus:ring-1 focus:ring-sage/30 rounded-xl px-4 py-3 font-manrope text-sm text-charcoal placeholder:text-charcoal-soft outline-none transition-all resize-none" />
                  </div>
                  <button type="submit" disabled={loading} data-testid="contact-submit"
                    className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60">
                    {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Send size={16} /> {page.submit_label}</>}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
